import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSIIFE = () => {
    const basicIIFECode = `// Standard IIFE (Anonymous)
(function() {
    const privateVar = "I'm hidden";
    console.log("IIFE Executed!");
})();

// IIFE with Parameters (Dependency Injection)
(function(global, $) {
    // Safely use $ without worrying about global conflict
    console.log("Global location:", global.location.href);
})(window, jQuery);`;

    const modulePatternCode = `// The Revealing Module Pattern
const AuthModule = (function() {
    let _user = null; // Private variable (convention: prefix with _)

    const _validateToken = (token) => token.length &gt; 10; // Private method

    return {
        login(user, token) {
            if (_validateToken(token)) {
                _user = user;
                return true;
            }
            return false;
        },
        getUser: () => _user
    };
})();

AuthModule.login('admin', 'secure-token-123');
console.log(AuthModule.getUser()); // 'admin'`;

    const asyncIIFECode = `// Modern Use Case: Async Initialization
(async () => {
    try {
        const config = await fetch('/api/config').then(r => r.json());
        console.log('App initialized with config:', config);
    } catch (err) {
        console.error('Initialization failed', err);
    }
})();`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Patterns & Encapsulation</div>
                <h1>IIFE & Function Scope</h1>
                <p className="hero-lead">
                    The <strong>Immediately Invoked Function Expression (IIFE)</strong> was once the
                    backbone of JavaScript module systems. Even in the era of ES Modules, it
                    remains a vital tool for <strong>scope isolation</strong> and
                    <strong>async initialization</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>The Mechanics of Isolation</h2>
                <p>
                    An IIFE is a function that runs as soon as it is defined. Historically,
                    this was the primary way to prevent <strong>Global Scope Pollution</strong>
                    before <code>let</code>, <code>const</code>, and Modules were introduced.
                </p>
                <CodeBlock language="javascript" code={basicIIFECode} />
                <ProTip><strong> Engineer's insight:</strong> The surrounding parentheses are
                    syntactically required. Without them, the engine sees the keyword
                    <code>function</code> and expects a declaration. The parentheses force
                    the engine to treat it as an <strong>expression</strong>, which can
                    then be invoked immediately.</ProTip>
            </div>

            <div className="content-section">
                <h2>The Revealing Module Pattern</h2>
                <p>
                    Before ES Modules (<code>import/export</code>), the
                    <strong>Revealing Module Pattern</strong> was the industry standard for
                    creating private state in JavaScript. It relies on the fact that variables
                    defined inside an IIFE are inaccessible from the outside world.
                </p>
                <CodeBlock language="javascript" code={modulePatternCode} />
                <div className="key-point">
                    This pattern creates a <strong>Closure</strong>. Even after the IIFE has
                    finished executing, the returned object still has a reference to the
                    private variables <code>_user</code> and <code>_validateToken</code>.
                </div>
            </div>

            <div className="content-section">
                <h2>Modern Use Case: Async IIFEs</h2>
                <p>
                    In modern environments (like Node.js or older browser bundles), you cannot
                    use the <code>await</code> keyword at the top level of a file. The
                    <strong>Async IIFE</strong> is the standard workaround to run
                    asynchronous logic immediately upon script load.
                </p>
                <CodeBlock language="javascript" code={asyncIIFECode} />
            </div>

            <div className="content-section">
                <h2>Why IIFEs are still relevant:</h2>
                <h3>1. Avoiding Name Collisions</h3>
                <p>
                    When using multiple libraries that might use the same global variable
                    (like <code>$</code> for jQuery and Zepto), an IIFE allows you to alias
                    them safely within a local scope.
                </p>
                <h3>2. Logic Isolation</h3>
                <p>
                    For scripts that perform a single task (like a tracking pixel or
                    configuration loader), an IIFE ensures that temporary variables are
                    <strong>Garbage Collected</strong> immediately after execution,
                    saving memory.
                </p>
                <h3>3. Minification Optimization</h3>
                <p>
                    Minifiers (like UglifyJS or Terser) can aggressively rename variables
                    inside an IIFE. By passing globals into an IIFE as arguments, you
                    allow the minifier to shorten those local references throughout the block.
                </p>
            </div>

            <KeyFacts><ul>
                    <li><strong>Syntax:</strong> <code>{"(function() {... })()"}</code></li>
                    <li><strong>Privacy:</strong> Variables are not hoisted to the global object.</li>
                    <li><strong>Modern Use:</strong> Primarily for <code>async/await</code> wrappers.</li>
                    <li> <strong>Obsolescence:</strong> Use ES Modules (<code>type="module"</code>) for complex app structure.</li>
                    <li><strong>Minification:</strong> Enables better compression of dependency references.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSIIFE;



